import { EmeraldDialogTypes } from 'src/common/enumerations/emerald-dialog-types';
import { getDefaultWeightUnitOfMeasure } from 'src/common/helpers/localized-description-helper';
import { popupMessage } from 'src/messages/business-logic/popup-message';
import { EntryMethod } from '../../common/enumerations/entry-method';
import { UnitOfMeasureTypes } from '../../common/enumerations/unit-of-measure-type';
import { convertStringToNumber, isNumber } from '../../common/helpers/number-helper';
import { validateWeight } from '../../common/helpers/weight-uom-validate';
import { IItemInputData } from '../../common/interfaces/IItemInputData';
import { IKeypadState } from '../../common/interfaces/states/IKeypadState';
import { addItemToTransaction } from '../../home-page/business-logic/sale-mode-router';
import { initiateTendering } from '../../tendering/business-logic/initiate-tendering';
import { withSpinner } from 'src/common/components/with-spinner/with-spinner';
import { IMessageModal } from 'src/common/interfaces/dtos/IMessageModal';
import { ISalesKeypadModifiers } from '../components/sales-keypad';
import { getSalesKeypadState } from './sales-keypad-operations';
import { getMaxWeight } from 'src/common/components/live-weight-area/helper/get-weight-unit';
import { store } from 'src/common/redux/configure-store';
import { ScaleStatus } from 'src/devices/device-enums';

export const addInputData = async (input: string, quantity?: number): Promise<void> => {
  const { metricValue, quantityEntered, isManualWeight } = getSalesKeypadState();
  const itemInputData: IItemInputData = {
    itemId: input,
    entryMethod: EntryMethod.Keyed,
    quantity: quantity ?? metricValue,
    quantityEntryMethod: quantityEntered ? EntryMethod.Keyed : EntryMethod.Automatic,
    isManualWeight: isManualWeight,
  };

  // If there's a weight on the scale, us that rather than manual weight.
  const scale = store.getState().scale;
  if (scale.status === ScaleStatus.STABLE_WEIGHT) {
    itemInputData.isManualWeight = false;
    itemInputData.quantity = 0;
  }
  await withSpinner(async () => {
    await addItemToTransaction(itemInputData);
  });
};

export const onSalesKeypadSubmit = async (input: string): Promise<void> => {
  if (!input) {
    await initiateTendering();
  } else {
    await addInputData(input).catch(popupMessage);
  }
};

export const updateUnitOfMeasure = (uom: UnitOfMeasureTypes, itemCode: string): IKeypadState => {
  const keypadState: IKeypadState = {
    metricValue: 1,
    itemCode: '',
    type: uom,
    isManualWeight: false,
    quantityEntered: false,
    visible: true,
  };

  if (validateWeight(uom)) {
    keypadState.isManualWeight = true;
  } else if (uom === UnitOfMeasureTypes.Quantity) {
    keypadState.quantityEntered = true;
  }
  if (keypadState.isManualWeight && isNumber(itemCode)) {
    const maxWeight = getMaxWeight(store.getState().scale.maxWeight);
    if (Number(itemCode) > maxWeight) {
      const error: IMessageModal = {
        message: 'POSKeyPad_InvalidWeight',
        type: EmeraldDialogTypes.Error,
        subMessage: 'POSKeyPad_LowerManualWeight',
      };
      popupMessage(error);
      // Reset keypad state so user can enter an item code
      keypadState.isManualWeight = false;
      keypadState.quantityEntered = true;
      keypadState.type = UnitOfMeasureTypes.Quantity;
    } else {
      keypadState.metricValue = Number(itemCode);
    }
  } else {
    keypadState.metricValue = Number(itemCode);
  }

  return keypadState;
};

export const onManualWeightClick = (value: string): IKeypadState => {
  return updateUnitOfMeasure(getDefaultWeightUnitOfMeasure() as UnitOfMeasureTypes, value);
};

export const onQuantityClick = (value: string): IKeypadState | undefined => {
  const keypadString = value;
  const convertedKeyedNumber = convertStringToNumber(keypadString);
  if (keypadString.match('^[0-9]*.?0*$') && convertedKeyedNumber) {
    return updateUnitOfMeasure(UnitOfMeasureTypes.Quantity, value);
  } else {
    if (convertedKeyedNumber === 0) {
      const error: IMessageModal = {
        message: 'POSKeyPad_IllegalQuantity',
        type: EmeraldDialogTypes.Error,
        subMessage: 'POSKeyPadControl_GreaterThanZero',
      };
      popupMessage(error);
    } else {
      const error: IMessageModal = {
        message: 'POSKeyPad_IllegalQuantity',
        type: EmeraldDialogTypes.Error,
        subMessage: 'POSKeyPadControl_DecimalQuantity',
      };
      popupMessage(error);
    }
  }
};

export const resetSalesKeypadModifiers = (
  salesKeypadModifiers: ISalesKeypadModifiers,
  defaultValues?: ISalesKeypadModifiers,
): void => {
  salesKeypadModifiers.setManualWeight?.(defaultValues?.isManualWeight ?? false);
  salesKeypadModifiers.setMetricValue?.(defaultValues?.metricValue ?? 1);
  salesKeypadModifiers.setType?.(defaultValues?.type ?? UnitOfMeasureTypes.Quantity);
  salesKeypadModifiers.setQuantityEntered(defaultValues?.isQuantityEntered ?? false);
};
